import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Compass, MapPin, Building } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import InterestModal from './InterestModal';
import { useSiteSettings, buildWhatsAppUrl } from '../lib/settings';

const PropertyCard = ({ p, compact = false }) => {
  const [open, setOpen] = useState(false);
  const { whatsapp_number } = useSiteSettings();
  return (
    <div className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
      <Link to={`/property/${p.id}`} className="relative aspect-[4/3] overflow-hidden bg-gray-100 block">
        <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3">
          <Badge className="bg-white/95 text-[#9a1b2f] hover:bg-white shadow-sm font-semibold border-0">{p.status}</Badge>
        </div>
        <div className="absolute top-3 right-3">
          <Badge className="bg-[#9a1b2f] text-white hover:bg-[#9a1b2f] shadow-sm border-0 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Verified
          </Badge>
        </div>
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="outline" className="text-[10px] capitalize border-gray-200">{p.type}</Badge>
          <Badge variant="outline" className="text-[10px] border-gray-200 flex items-center gap-1"><Compass className="w-2.5 h-2.5" />{p.facing}</Badge>
          <Badge variant="outline" className="text-[10px] capitalize border-gray-200">{p.furnishing}</Badge>
        </div>
        <Link to={`/property/${p.id}`}>
          <h3 className="font-bold text-gray-900 text-lg leading-tight mb-2 line-clamp-1 group-hover:text-[#9a1b2f] transition-colors">{p.name}</h3>
        </Link>
        <div className="space-y-1.5 text-xs text-gray-600">
          <div className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded bg-gray-100 font-semibold text-gray-700">{p.bhk}</span>
            <span className="flex items-center gap-1 line-clamp-1"><MapPin className="w-3 h-3" /> {p.location}</span>
          </div>
          <div className="flex items-center gap-3">
            <span>{p.sqft} sqft</span>
            <span className="flex items-center gap-1"><Building className="w-3 h-3" />{p.floor}</span>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
              <img src={p.builder_logo} alt={p.builder} className="w-full h-full object-contain p-0.5" />
            </div>
            <span className="text-xs font-semibold text-gray-700 line-clamp-1">{p.builder}</span>
          </div>
          <div className="text-right shrink-0">
            {p.old_price && <div className="text-[10px] text-gray-400 line-through">{p.old_price}</div>}
            <div className="text-base font-black text-[#9a1b2f] leading-tight">{p.price}</div>
            {p.negotiable && <div className="text-[10px] text-emerald-600 font-semibold">Negotiable</div>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button size="sm" variant="outline" className="h-8 text-xs font-semibold border-[#9a1b2f] text-[#9a1b2f] hover:bg-[#9a1b2f] hover:text-white" onClick={() => setOpen(true)}>Interested?</Button>
          <a
            href={buildWhatsAppUrl(whatsapp_number, `Hi, I'm interested in ${p.name} (${p.price})`)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button size="sm" className="h-8 text-xs font-semibold bg-[#25D366] hover:bg-[#20b955] text-white w-full">WhatsApp</Button>
          </a>
        </div>
      </div>
      <InterestModal open={open} onOpenChange={setOpen} property={p} />
    </div>
  );
};

export default PropertyCard;
